'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import Image from 'next/image';

import { gsap } from 'gsap';

import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

/**
 * The people behind the written testimonials, as a still row with a focus.
 *
 * Sierra runs its customers' faces as an endless marquee. This started there
 * and moved off it: a rail that never stops means the reader is always either
 * waiting for someone or watching them leave, and with five people there is
 * nothing to wait for — they all fit. So the row stands still and exactly one
 * card is presented at a time. The rest recede.
 *
 * That "exactly one" is the whole idea. A row where every card is the same
 * size is a contact sheet; a row where one card is forward is a stage, and
 * the reader's eye is told where to start. Which card is forward follows the
 * cursor, so the row answers to the reader rather than to a timer.
 *
 * GSAP drives the transition rather than a CSS class swap, because the change
 * is four properties moving together on two cards at once — the one stepping
 * forward and the one stepping back — and a spring-ish ease on that reads as
 * weight. A CSS transition gets the geometry right and the feel wrong.
 *
 * A card carries the portrait, the name and the role. Not the quote: these
 * run to two hundred characters, and the quote is one click away on the story
 * page, which is where someone who wants it is going anyway.
 */

export interface Person {
  /** Story slug; the card links to it. */
  slug: string;
  /**
   * The story's own attribution line — "Sven Braam, CEO at Snel Een Taxi" —
   * passed through whole from the same i18n key the story page quotes, so a
   * name or a title can never drift between the two places it appears.
   */
  attribution: string;
  /** Portrait from /public/images/customer-stories/portraits. Required: a
   *  card without a photograph is a placeholder wearing someone's name, and
   *  this row is a row of faces or it is nothing. */
  portrait: string;
  company: string;
}

/** The presented card. Forward, lifted, at full strength. */
const ACTIVE = { scale: 1.06, y: -22, opacity: 1 };
/** Everyone else. Far enough back to be clearly secondary, near enough to
 *  still be a face rather than a swatch. */
const RESTING = { scale: 0.94, y: 0, opacity: 0.55 };

const TRANSITION = { duration: 0.55, ease: 'power3.out' };

/**
 * The attribution is one string because that is how it is written, reviewed
 * and translated. Splitting on the first separator — Latin comma or Arabic —
 * gives the name its own line without keeping the two halves in two keys that
 * can disagree. No separator: the whole line is the name, and the role line
 * simply does not render.
 */
const splitAttribution = (attribution: string) => {
  const at = attribution.search(/[,،]/);
  if (at === -1) return { name: attribution.trim(), role: '' };
  return {
    name: attribution.slice(0, at).trim(),
    role: attribution
      .slice(at + 1)
      .trim()
      .replace(/^[,،]\s*/, ''),
  };
};

/**
 * Read straight from the media query rather than mirroring it into state in
 * an effect, which would render once with the wrong answer and then again
 * with the right one. The server has no media queries, so it gets `false` and
 * agrees with the first client render.
 */
const useReducedMotion = () => {
  const subscribe = useCallback((notify: () => void) => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    query.addEventListener('change', notify);
    return () => query.removeEventListener('change', notify);
  }, []);

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );
};

/**
 * The first paint has to land on the right card or the reader sees one card
 * step forward and another take over. That means measuring before the browser
 * paints, which is a layout effect — and layout effects do not exist on the
 * server, so it falls back to a normal effect there and the warning with it.
 */
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

const PersonCard = ({
  person,
  isRTL,
  active,
  register,
  onFocus,
}: {
  person: Person;
  isRTL: boolean;
  active: boolean;
  register: (el: HTMLElement | null) => void;
  onFocus: () => void;
}) => {
  const { name, role } = splitAttribution(person.attribution);

  return (
    <Link
      ref={register}
      href={`/customer-stories/${person.slug}`}
      prefetch={active}
      onPointerEnter={onFocus}
      onFocus={onFocus}
      className={cn(
        'focus-visible:ring-foreground block rounded-2xl outline-hidden focus-visible:ring-2 focus-visible:ring-offset-4',
        // A fixed width at every size, and a generous one — roughly what
        // Sierra gives a card. Splitting the container five ways instead
        // capped a portrait at 233px, which is a thumbnail: too small to read
        // a face, let alone an expression, on the one part of the page whose
        // whole job is that the customers are people. Past three or so the
        // row runs off the side and scrolls, which is the right trade — a
        // face you can see beats a row you can count.
        'w-[280px] shrink-0 snap-center md:w-[330px] xl:w-[360px]',
        // The lift is around the bottom edge, so a card grows upward out of a
        // fixed baseline rather than swelling in both directions. That is what
        // makes the row read as one shelf with someone stepping forward on it.
        'origin-bottom will-change-transform',
        // The presented card passes in front of its neighbours rather than
        // being clipped by the one after it in source order.
        active ? 'z-20' : 'z-0',
      )}
    >
      <figure
        className={cn(
          'bg-sand-100 relative aspect-[3/4] overflow-hidden rounded-2xl',
          'transition-shadow duration-500',
          active ? 'shadow-2xl shadow-black/15' : 'shadow-none',
        )}
      >
        <Image
          src={person.portrait}
          alt=""
          fill
          sizes="(min-width: 1280px) 360px, (min-width: 768px) 330px, 280px"
          // Colour, deliberately. These faces are the warmest thing on a page
          // that is otherwise marks and numbers, and desaturating them bought a
          // consistency nobody asked for at the price of the only warmth on the
          // page. The greyscale originals two of them shipped with have since
          // been replaced rather than filtered — a filter could never have gone
          // the other way.
          className="object-cover object-top"
        />

        {/* Only where the type lands. A scrim across a portrait flattens the
            face; this one starts below the chin on a 3:4 crop. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/25 to-transparent"
        />

        <figcaption
          className={cn(
            'absolute inset-x-0 bottom-0 flex flex-col gap-0.5 p-5 md:p-6',
            isRTL ? 'text-right' : 'text-left',
          )}
        >
          <span className="font-serif text-lg leading-tight text-white md:text-xl">
            {name}
          </span>
          {role ? (
            <span className="text-sm leading-snug text-white/75">{role}</span>
          ) : null}
        </figcaption>
      </figure>
    </Link>
  );
};

export const TestimonialWall = ({
  id,
  heading,
  people,
  isRTL,
}: {
  /** Anchor for the top-of-page snap; see `SnapPastHero`. */
  id?: string;
  heading: string;
  people: Array<Person>;
  isRTL: boolean;
}) => {
  const rail = useRef<HTMLDivElement>(null);
  const cards = useRef<Array<HTMLElement | null>>([]);
  const reduced = useReducedMotion();

  /**
   * The middle of the list, as a starting guess for the server and the first
   * client render. It is only a guess: once the row is laid out, the card in
   * the middle of the *view* is the one that gets presented, and with cards
   * this size the two are not the same — the row overflows, so the third of
   * five sits off to the right rather than in front of the reader.
   */
  const [active, setActive] = useState(() => Math.floor(people.length / 2));

  /** Index of the card nearest the middle of the rail, right now. */
  const centremost = useCallback(() => {
    const railEl = rail.current;
    if (!railEl) return 0;
    const railBox = railEl.getBoundingClientRect();
    const middle = railBox.left + railBox.width / 2;
    let nearest = 0;
    let best = Infinity;
    cards.current.forEach((node, i) => {
      if (!node) return;
      const box = node.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - middle);
      if (distance < best) {
        best = distance;
        nearest = i;
      }
    });
    return nearest;
  }, []);

  /**
   * Correct the guess before the first paint, and again whenever the row is
   * re-laid out — a resize changes both how many cards fit and which one is
   * in the middle of them.
   */
  useIsomorphicLayoutEffect(() => {
    setActive(centremost());
    const onResize = () => setActive(centremost());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [centremost, people.length]);

  useEffect(() => {
    const nodes = cards.current;
    const context = gsap.context(() => {
      nodes.forEach((node, i) => {
        if (!node) return;
        const target = i === active ? ACTIVE : RESTING;
        // Reduced motion still gets the hierarchy — it is depth, not movement
        // — but arrives at it without the travel.
        if (reduced) gsap.set(node, target);
        else gsap.to(node, { ...target, ...TRANSITION });
      });
    }, rail);

    return () => context.revert();
  }, [active, reduced, people.length]);

  /**
   * The row is a scroll rail at every size now that the cards are big enough
   * to overflow it, and the card nearest the middle is the one presented — so
   * a thumb chooses on a touch screen exactly as the cursor does with a
   * pointer. Whichever happened last wins, which is what a reader expects.
   */
  useEffect(() => {
    const railEl = rail.current;
    if (!railEl) return;

    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setActive(centremost()));
    };

    railEl.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      railEl.removeEventListener('scroll', onScroll);
    };
  }, [centremost]);

  return (
    <section
      id={id}
      data-nav-theme="light"
      className="bg-background py-16 md:py-24"
    >
      <div className="container">
        <h2 className="font-serif text-foreground mb-10 text-2xl leading-tight font-normal md:text-[44px] md:tracking-[-0.01em]">
          {heading}
        </h2>

        {/* Padding above for the lift and below for the shadow, so neither is
            clipped by the rail's own overflow. */}
        <div
          ref={rail}
          className={cn(
            // Headroom for the presented card, which grows upward out of its
            // bottom edge. A 480px card at scale 1.06 gains 29px above the
            // row, then the lift takes another 22 — 51px in all, and pt-10
            // gave it 40, so the top of Melvin's hair was being sliced off.
            // `overflow-x: auto` forces `overflow-y` to `auto`, so the rail
            // clips vertically whether or not it was asked to; this padding
            // is the only thing standing between the scale and a haircut.
            // Revisit it if ACTIVE.scale or ACTIVE.y change.
            'flex pt-16 pb-12',
            'snap-x snap-mandatory overflow-x-auto',
            '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            // The rail overflows its column by the container's own gutter, so
            // a card leaving the row runs to the edge of the screen rather
            // than stopping short of it, while the first card still lines up
            // with the heading above it and the wall below.
            '-mx-4 px-4 md:-mx-6 md:px-6',
          )}
        >
          {/* The track, centred by auto margins rather than `justify-center`.
              A centred flex container whose content overflows puts the first
              item off the left edge and out of scroll range — the classic
              flexbox scrolling bug. Auto margins centre the track while it
              fits and collapse to nothing once it does not, so two cards sit
              in the middle and six still scroll from the first. */}
          <div className="mx-auto flex w-max items-end gap-4">
            {people.map((person, i) => (
              <PersonCard
                key={person.slug}
                person={person}
                isRTL={isRTL}
                active={i === active}
                onFocus={() => setActive(i)}
                register={(el) => {
                  cards.current[i] = el;
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
